import { PlusIcon } from 'lucide-react'
import { useEffect } from 'react'
import useSWR from 'swr'

import { DesktopNavigation, MobileNavigation } from '../components/Navigation'
import { Sheet } from '../components/Sheet'
import { useRouter, useSearchParams } from '../lib/navigation'
import { api } from '../lib/password'
import { fetcher } from '../lib/swr'
import DbOrderHeader from './DbOrderHeader'
import {
  DragDrop,
  SortableOrderSection,
  SortableOrders,
  SortablePlanList,
} from './DragDrop'
import OrderDetail from './OrderDetail'
import PlanDetail from './PlanDetail'
import RecipeSelect from './RecipeSelect'
import type { FullOrder, FullPlan, Order } from './types'

/** Planning: "In huis" + shopping orders — two columns on desktop, stacked on mobile. */
export default function Home() {
  const router = useRouter()
  const search = useSearchParams()
  const home = useSWR<FullPlan[]>('/api/plans?view=home', fetcher)
  const orders = useSWR<FullOrder[]>('/api/orders', fetcher)

  useEffect(() => {
    const isMobile = /Android/i.test(navigator.userAgent)
    if (!isMobile) return
    document.body.classList.add('android')
    return () => document.body.classList.remove('android')
  }, [])

  const orderAdd = search.get('orderAdd')
  const orderIdParam = search.get('order') || search.get('orderId')
  const orderId = orderIdParam ? parseInt(orderIdParam) : null
  const planSelected = search.get('plan') ? parseInt(search.get('plan')!) : null

  const shopping = (orders.data || []).map((order, i) => ({
    ...order,
    display: orderName(order),
    even: i % 2 === 1,
    active: order.plannedAt && Date.parse(order.plannedAt) < Date.now(),
    planned: order.plannedAt && Date.parse(order.plannedAt) > Date.now(),
  }))

  const calculateCourseTotals = (plans?: FullPlan[]) => {
    let totalPortions = 0
    let totalOutFreezer = 0
    let totalInFreezer = 0

    plans?.forEach((p) => {
      const recipe = p.recipe
      if (
        !recipe ||
        !['hoofdgerecht', 'slaatje', 'diepvries gerecht'].includes(
          recipe.course!
        )
      )
        return
      const portionsEat = p.portionsEat ?? recipe.portionsEat ?? 2
      const portionsFreezer = p.portionsFreezer ?? recipe.portionsFreezer ?? 0
      const multiplier = p.multiplier ?? 1
      const multiplierFreezer = p.multiplierFreezer ?? 1

      const freezerTotal = multiplierFreezer * portionsFreezer
      if (p.multiplierFreezer! === -p.multiplier!) {
        totalOutFreezer += -multiplierFreezer * portionsFreezer
      } else if (freezerTotal > 0) {
        totalInFreezer += freezerTotal
        totalPortions += multiplier * portionsEat
      } else {
        totalPortions += multiplier * portionsEat
      }
    })

    if (totalPortions === 0 && totalOutFreezer === 0 && totalInFreezer === 0) {
      return ''
    }

    const parts = []
    if (totalPortions > 0) parts.push(`${totalPortions} porties`)
    if (totalOutFreezer > 0) parts.push(`${totalOutFreezer} uit diepvries`)
    if (totalInFreezer > 0) parts.push(`${totalInFreezer} in diepvries`)
    return parts.join(' · ')
  }

  const homeSummary = calculateCourseTotals(home.data)

  function closeModal() {
    router.replace('/planning')
  }

  function refresh() {
    home.mutate()
    orders.mutate()
  }

  return (
    <DragDrop orders={orders} home={home}>
      <Sheet
        open={Boolean(planSelected || orderAdd || orderId)}
        onClose={closeModal}
        title={orderAdd && !planSelected ? 'Recept toevoegen' : undefined}
        wide
        sheet={
          planSelected ? (
            <PlanDetail
              onChange={refresh}
              onClose={closeModal}
              planId={planSelected}
              plan={
                home.data?.find((p) => p.id == planSelected) ||
                orders.data
                  ?.flatMap((c) => c.plans)
                  .find((p) => p.id == planSelected)
              }
            />
          ) : orderAdd ? (
            <RecipeSelect
              onPlan={async (input) => {
                await api.post('/api/plans', {
                  orderId: orderAdd === 'home' ? -1 : parseInt(orderAdd!),
                  ...input,
                })
              }}
              onChange={refresh}
              onClose={closeModal}
            />
          ) : orderId ? (
            <OrderDetail
              key={orderId}
              orderId={orderId}
              order={orders.data?.find((c) => c.id === orderId)!}
              onChange={refresh}
              onClose={closeModal}
            />
          ) : null
        }
      >
        <main className="min-h-screen flex flex-col text-white">
          <DesktopNavigation />

          <div className="flex-1 w-full max-w-5xl lg:max-w-6xl mx-auto px-4 pt-4 pb-28 md:px-6 md:pt-6 md:pb-16">
            <div className="lg:grid lg:grid-cols-2 lg:gap-12 xl:gap-16 lg:items-start">
              <section className="mb-10 lg:mb-0">
                <DbOrderHeader
                  order={{ display: 'In huis' }}
                  summary={homeSummary}
                />
                <SortablePlanList plans={home.data} />
              </section>

              <div className="min-w-0">
                <SortableOrders orders={shopping}>
                  {shopping.map((order) => (
                    <SortableOrderSection
                      key={order.id}
                      order={order}
                      summary={calculateCourseTotals(order.plans)}
                    >
                      <SortablePlanList
                        plans={order.plans}
                        orderId={order.id}
                      />
                    </SortableOrderSection>
                  ))}
                </SortableOrders>

                <div className="flex flex-col gap-3 mt-4">
                  <button
                    type="button"
                    className="w-full rounded-2xl bg-white/10 hover:bg-white/15 px-4 py-4 text-left font-medium transition-colors flex items-center gap-2"
                    onClick={async () => {
                      const display = prompt(
                        'Naam van de bestelling',
                        'Shopping lijst'
                      )
                      if (!display) return
                      const days = home.data?.length || 1
                      const plannedAt = Date.now() + days * 24 * 60 * 60 * 1000
                      await api.post('/api/orders', { display, plannedAt })
                      orders.mutate()
                    }}
                  >
                    <PlusIcon className="w-5 h-5" /> Nieuwe bestelling
                  </button>
                </div>
              </div>
            </div>
          </div>

          <MobileNavigation />
        </main>
      </Sheet>
    </DragDrop>
  )
}

function orderName(order: Order) {
  if (order.display) return order.display
  if (order.plannedAt) {
    if (Date.parse(order.plannedAt) < Date.now()) return 'Menu'
    return 'Shopping list'
  }
  return 'Bestelling'
}
